<script setup>
import Header from '@/components/Header.vue'
import Footer from '@/components/Footer.vue'
import Helmet from '@/components/Helmet.vue'
import { useRouter, useRoute } from 'vue-router'

const router = useRouter()
const route = useRoute()

function goToContact() {
  if (route.path === '/') {
    // Si ya estás en la página principal, simplemente agregamos el hash
    router.replace({ hash: '#contact' })
  } else {
    // Si estás en otra ruta, redirige a la home con el hash
    router.push({ path: '/', hash: '#contact' })
  }
}
</script>

<template>
  <Helmet
    title="Qué es el mantenimiento web y por qué lo necesitás todos los meses | Resi Corporation"
    description="Descubrí por qué el mantenimiento web regular es esencial para mantener tu sitio seguro, rápido, actualizado y bien posicionado en Google."
  />
  <Header />

  <section class="container">
    <h2 class="title">¿Qué es el mantenimiento web y por qué lo necesitás todos los meses?</h2>

    <p class="summary">
      Descubrí por qué una web sin mantenimiento queda obsoleta y cómo un plan mensual protege tu inversión.
    </p>

    <div class="meta">
      <span class="author">Por Máximo Felipe Resi</span>
      <span class="date">9 de junio de 2025</span>
    </div>

    <div class="image-wrapper">
      <img src="../../public/img/mantenimiento.png"
           alt="Equipo realizando mantenimiento web" title="Mantenimiento web mensual" />
    </div>

    <article class="content">
      <p>
        Tener una página web no significa que el trabajo esté terminado. Con el tiempo, las tecnologías cambian, los sistemas operativos evolucionan, aparecen nuevas amenazas de seguridad y los algoritmos de Google se actualizan constantemente. Si no hacés mantenimiento regular, tu sitio se vuelve lento, inseguro y pierde posiciones en los buscadores.
      </p>

      <h2>🛠️ Qué tareas incluye un buen mantenimiento web</h2>
      <ul>
        <li><strong>Actualizaciones de seguridad y software:</strong> Es vital mantener actualizados los frameworks, librerías y dependencias utilizadas en el desarrollo del sitio (por ejemplo, Vue, React, librerías de animación, paquetes de npm, etc.) para evitar vulnerabilidades de seguridad y fallos de compatibilidad.</li>
        <li><strong>Backups regulares:</strong> Una copia de seguridad semanal o incluso diaria permite restaurar el sitio rápidamente ante cualquier inconveniente.</li>
        <li><strong>Escaneos de seguridad:</strong> Detectan malware o archivos maliciosos que podrían afectar tanto al sitio como a los visitantes.</li>
        <li><strong>Optimización de velocidad:</strong> Limpiar caché, comprimir imágenes y mejorar el tiempo de carga es clave para una buena experiencia de usuario.</li>
        <li><strong>Revisión de enlaces y formularios:</strong> Detectar links rotos o formularios que no funcionan evita frustraciones en tus usuarios.</li>
        <li><strong>Actualización de contenido y SEO:</strong> Revisar títulos, textos, meta descripciones y palabras clave asegura que sigas siendo competitivo en los motores de búsqueda.</li>
        <li><strong>Monitorización de uptime:</strong> Asegurarse de que tu sitio esté en línea 24/7 mediante herramientas automáticas.</li>
        <li><strong>Revisiones periódicas de accesibilidad y usabilidad:</strong> Especialmente útil para cumplir con normas legales o mejorar la experiencia de los usuarios.</li>
      </ul>

      <h2>📅 Frecuencias recomendadas</h2>
      <table>
        <thead>
          <tr>
            <th>Tarea</th>
            <th>Frecuencia sugerida</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>Actualizaciones y escaneos</td><td>1–4 veces por mes</td></tr>
          <tr><td>Backups</td><td>Semanal o diario si hay cambios frecuentes</td></tr>
          <tr><td>Optimización y pruebas</td><td>Mensual</td></tr>
          <tr><td>SEO y contenido</td><td>Trimestral</td></tr>
          <tr><td>Auditorías integrales</td><td>Semestral o anual</td></tr>
        </tbody>
      </table>

      <h2>✅ Beneficios reales del mantenimiento web</h2>
      <ul>
        <li><strong>Seguridad:</strong> Se estima que más del 60 % de los sitios hackeados tenían software desactualizado.</li>
        <li><strong>Velocidad:</strong> Cerca del 50 % de los usuarios abandonan un sitio si tarda más de 3 segundos en cargar.</li>
        <li><strong>Mejor posicionamiento:</strong> Google favorece sitios que cargan rápido, son seguros y tienen contenido actualizado.</li>
        <li><strong>Confianza del usuario:</strong> Una web sin errores transmite profesionalismo y mejora las conversiones.</li>
        <li><strong>Ahorro a largo plazo:</strong> Un mantenimiento mensual es más económico que reparar un sitio hackeado o caído.</li>
      </ul>

      <h2>🔁 Rutina mensual recomendada</h2>
      <p><strong>Mes 1:</strong> Actualización de software, escaneo de seguridad, limpieza de caché y backup general.</p>
      <p><strong>Mes 2:</strong> Pruebas de formularios, revisión de enlaces rotos y análisis de velocidad.</p>
      <p><strong>Mes 3:</strong> Optimización de contenido, revisión SEO y ajuste de etiquetas.</p>
      <p><strong>Mes 6:</strong> Auditoría completa de diseño, usabilidad, accesibilidad y cumplimiento normativo.</p>

      <h2>¿Podés hacerlo vos o necesitás ayuda profesional?</h2>
      <p>
        Si tenés conocimientos técnicos y tu web es sencilla, podés encargarte. Pero si tu sitio maneja ventas, reservas o gran cantidad de tráfico, conviene delegar el mantenimiento a especialistas. En Resi Corporation ofrecemos seguimiento profesional, tiempos de respuesta rápidos e informes mensuales para que no tengas que preocuparte por nada.
      </p>

      <h2>📌 Conclusión</h2>
      <p class="engagement">
        El mantenimiento web es mucho más que una formalidad. Es una protección constante para tu inversión digital. Si lo dejás de lado, tu sitio pierde calidad, seguridad y efectividad. Si lo hacés bien, tu negocio crece más tranquilo, seguro y profesional.
      </p>

      <div class="cta-wrapper">
        <a @click.prevent="goToContact" href="#" class="cta-link">Solicitá tu plan de mantenimiento</a>
      </div>

    </article>
  </section>

  <Footer />
</template>

<style scoped>
.container {
  margin-top: 120px !important;
  max-width: 800px;
  margin: 40px auto;
  padding: 0 20px;
}

.title {
  font-size: 2.4rem;
  font-weight: 700;
  margin-bottom: 10px;
}

.summary {
  font-size: 1.1rem;
  color: var(--text-color);
  margin-bottom: 20px;
  line-height: 1.6;
}

.meta {
  display: flex;
  justify-content: space-between;
  font-size: 0.9rem;
  color: var(--text-color);
  margin-bottom: 25px;
  border-bottom: 1px solid #ddd;
  padding-bottom: 10px;
}

.image-wrapper {
  margin-bottom: 30px;
}

.image-wrapper img {
  width: 100%;
  border-radius: 8px;
  object-fit: cover;
}

.content {
  font-size: 1.05rem;
  line-height: 1.8;
  color: var(--text-color);
}

.content h2 {
  font-size: 1.6rem;
  margin-top: 30px;
  margin-bottom: 15px;
}

.content p,
.content ul {
  margin-bottom: 20px;
}

.content ul {
  padding-left: 1.5rem;
}

.content ul li {
  margin-bottom: 0.7rem;
  line-height: 1.5;
}

.engagement {
  color: var(--title-color);
  font-weight: 600;
}

.cta-wrapper {
  text-align: center;
  margin-top: 30px;
}

.cta-link {
  display: inline-block;
  margin-top: 1rem;
  background: var(--first-color);
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  text-decoration: none;
  transition: background 0.3s ease;
}

.cta-link:hover {
  background: red;
}
</style>
